import { useState } from "react";
import { User, Address } from "@/types/ApiTypes";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { GiPresent } from "react-icons/gi";
import { AppConfig } from "@/utils/AppConfig";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";

type ClientsTableRowProps = {
    data: User;
};

export default function ClientsTableRow({ data }: ClientsTableRowProps) {
    const { t } = useTranslation();
    const apps = useSelector((state: RootState) => state.apps.AppsData);
    const loyaltyApp = apps?.find((app) => app.name === "loyalty");

    const [isModalOpen, setIsModalOpen] = useState(false);

    return (
        <>
            <tr className="bg-white border-b text-center">
                <th scope="row" className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">
                    {data.name}
                </th>
                <td className="px-6 py-4">{data.phone}</td>
                <td className="px-6 py-4">{data.email || t("clientsTable.noEmail")}</td>
                <td dir="rtl" className="px-6 py-4">
                    <div className="flex justify-center items-center gap-1">
                        {data.walletBalance} {AppConfig.currency.icon("currentcolor", 17)}
                    </div>
                </td>
                {loyaltyApp?.installed && (
                    <td dir="rtl" className="px-6 py-4">
                        <div className="flex justify-center items-center gap-1">
                            {data.loyaltyPoints} <GiPresent size={17} className="mb-1" />
                        </div>
                    </td>
                )}
                <td className="px-6 py-4">{data.ordersCount}</td>
                <td className="px-6 py-4">
                    <div className="flex flex-col gap-1 text-xs justify-center items-center">
                        {data.ordersCount && data.ordersCount > 0 ? (
                            <Link
                                to={`/clients/${data.id}`}
                                className="px-2 w-full py-1 bg-sidebar text-skyblue rounded-lg"
                            >
                                {t("clients.orderHistory")}
                            </Link>
                        ) : null}

                        {data.addresses.length > 0 && (
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="px-2 py-1 bg-skyblue text-sidebar rounded-lg w-full"
                            >
                                {t("clientsTable.address")}
                            </button>
                        )}
                    </div>
                </td>
            </tr>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-lg max-w-lg w-full p-4">
                        <h2 className="text-lg font-bold mb-4">{t("clientsTable.addresses")}</h2>
                        <ul className="space-y-3 max-h-80 overflow-y-auto border rounded-lg p-2 divide-y divide-gray-200">
                            {data.addresses.map((addr: Address, i: number) => (
                                <li
                                    key={addr._id || i}
                                    className="p-3 space-y-2 text-sm"
                                >
                                    {addr.isDefault && (
                                        <p className="text-green-600 font-semibold">
                                            {t("clientsTable.defaultAddress")}
                                        </p>
                                    )}
                                    <p>
                                        <strong>{t("clientsTable.city")}:</strong> {addr.city}
                                    </p>
                                    <p>
                                        <strong>{t("clientsTable.street")}:</strong> {addr.street}
                                    </p>
                                    <p>
                                        <strong>{t("clientsTable.building")}:</strong> {addr.buildingNumber} ({addr.buildingType})
                                    </p>
                                    {addr.apartmentNumber && (
                                        <p>
                                            <strong>{t("clientsTable.apartment")}:</strong> {addr.apartmentNumber}
                                        </p>
                                    )}
                                    {addr.floorNumber && (
                                        <p>
                                            <strong>{t("clientsTable.floor")}:</strong> {addr.floorNumber}
                                        </p>
                                    )}
                                    {addr.additionalInfo && (
                                        <p>
                                            <strong>{t("clientsTable.additional")}:</strong> {addr.additionalInfo}
                                        </p>
                                    )}
                                </li>
                            ))}
                        </ul>

                        <div className="mt-4 text-end">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="px-4 py-2 bg-red-500 rounded-lg text-white"
                            >
                                {t("close")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
